import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Box,
  Typography,
} from "@mui/material";
import {
  Tablecell,
  applySortFilter,
  getComparator,
  findStrategy,
  findFundAbbById,
} from "../../../../../component/Methods";
import { useSelector } from "react-redux";
import { selectFunds, selectStrategies, selectRebalancerSummary, selectActiveRequests } from "../../../../../Redux/Reducers/selectors";
import SummaryTableSkeleton from "../../../Skeletons/SummaryTableSkeleton";

const formatNumber = (value) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return value;
  const absNum = Math.abs(num);
  const formattedNumber = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(absNum);

  return num < 0 ? `(${formattedNumber})` : `${formattedNumber}\u00A0`;
};

const SummaryTable = ({ tableHeader }) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("column1");
  const [expandedRow, setExpandedRow] = useState(null);
  const [strategyData, setStrategyData] = useState({});
  const [selectedFundId, setSelectedFundId] = useState("All");
  const [selectedStrategyId, setSelectedStrategyId] = useState("All");
  const funds = useSelector(selectFunds);
  const rebalancerSummary = useSelector(selectRebalancerSummary);
 const activeRequests = useSelector(selectActiveRequests);
  const strategies = useSelector(selectStrategies);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleFundIdChange = (event) => {
    setSelectedFundId(event.target.value);
    setSelectedStrategyId("All");
    setExpandedRow(null);
  };

  const handleStrategyIdChange = (event) => {
    setSelectedStrategyId(event.target.value);
  };

  useEffect(() => {
    if (!selectedFundId && Object.keys(rebalancerSummary)?.length > 0) {
      const firstFundId = Object.keys(rebalancerSummary)[0];
      setSelectedFundId(firstFundId);
    }
  }, [rebalancerSummary, selectedFundId]);

  useEffect(() => {
    setSelectedStrategyId("All");
    setExpandedRow(null);
  }, [selectedFundId]);

  useEffect(() => {
    if (selectedFundId === "All") {
      const aggregatedData = Object.values(rebalancerSummary)?.reduce(
        (acc, fundData) => {
          const dataToSort = fundData?.totals || [];
          const sortedData = applySortFilter(
            dataToSort,
            getComparator(order, orderBy)
          );

          sortedData?.forEach((row) => {
            if (!acc[row.type]) {
              acc[row.type] = {
                ...row,
                tradeCount: 0,
                shares: 0,
                dollars: 0,
                marketTrades: 0,
                crossingTrades: 0,
                marketShares: 0,
                crossingShares: 0,
                marketDollars: 0,
                crossingDollars: 0
              };
            }

            acc[row.type].tradeCount += row.tradeCount;
            acc[row.type].shares += row.shares;
            acc[row.type].dollars += parseInt(row.dollars, 10);
            acc[row.type].marketTrades += row.marketTradeCount || 0;
            acc[row.type].crossingTrades += row.crossTradeCount || 0;
            acc[row.type].marketShares += row.marketShares || 0;
            acc[row.type].crossingShares += row.crossShares || 0;
            acc[row.type].marketDollars += parseInt(row.marketDollars || 0, 10);
            acc[row.type].crossingDollars += parseInt(row.crossDollars || 0, 10);
          });

          return acc;
        },
        {}
      );

      const aggregatedRows = Object.keys(aggregatedData)?.map((type) => {
        const row = aggregatedData[type];
        return {
          type,
          tradeCount: row?.tradeCount?.toString() || "0",
          shares: row?.shares?.toString() || "0",
          dollars: row?.dollars?.toString() || "0",
          marketTrades: row?.marketTrades?.toString() || "0",
          crossingTrades: row?.crossingTrades?.toString() || "0",
          marketShares: row?.marketShares?.toString() || "0",
          crossingShares: row?.crossingShares?.toString() || "0",
          marketDollars: row?.marketDollars?.toString() || "0",
          crossingDollars: row?.crossingDollars?.toString() || "0",
        };
      });

      const overallTotals = aggregatedRows?.reduce(
        (acc, row) => {
          acc.tradeCount += Math.abs(row?.tradeCount);
          acc.shares += Math.abs(row?.shares);
          acc.dollars += parseInt(row?.dollars, 10);
          acc.marketTrades += Math.abs(row?.marketTrades);
          acc.crossingTrades += Math.abs(row?.crossingTrades);
          acc.marketShares += Math.abs(row?.marketShares);
          acc.crossingShares += Math.abs(row?.crossingShares);
          acc.marketDollars += parseInt(row?.marketDollars, 10);
          acc.crossingDollars += parseInt(row?.crossingDollars, 10);
          return acc;
        },
        {
          tradeCount: 0,
          shares: 0,
          dollars: 0,
          marketTrades: 0,
          crossingTrades: 0,
          marketShares: 0,
          crossingShares: 0,
          marketDollars: 0,
          crossingDollars: 0
        }
      );

      const totalRow = {
        type: "Total",
        tradeCount: overallTotals?.tradeCount?.toString() || "0",
        shares: overallTotals?.shares?.toString() || "0",
        dollars: overallTotals?.dollars?.toString() || "0",
        marketTrades: overallTotals?.marketTrades?.toString() || "0",
        crossingTrades: overallTotals?.crossingTrades?.toString() || "0",
        marketShares: overallTotals?.marketShares?.toString() || "0",
        crossingShares: overallTotals?.crossingShares?.toString() || "0",
        marketDollars: overallTotals?.marketDollars?.toString() || "0",
        crossingDollars: overallTotals?.crossingDollars?.toString() || "0",
      };

      setData([...aggregatedRows, totalRow]);
      setStrategyData({});
    } else if (selectedFundId && rebalancerSummary[selectedFundId]) {
      let dataToSort = rebalancerSummary[selectedFundId]?.totals || [];
      
      // Filter by strategy if a specific strategy is selected
      if (selectedStrategyId !== "All") {
        const strategyTotals = rebalancerSummary[selectedFundId]?.strategyTotals?.[selectedStrategyId] || [];
        dataToSort = strategyTotals;
      }
      
      const sortedData = applySortFilter(
        dataToSort,
        getComparator(order, orderBy)
      );

      // Transform backend field names to frontend field names
      const transformedData = sortedData?.map((row) => ({
        type: row.type,
        tradeCount: row.tradeCount?.toString() || "0",
        shares: row.shares?.toString() || "0",
        dollars: row.dollars?.toString() || "0",
        marketTrades: row.marketTradeCount?.toString() || "0",
        crossingTrades: row.crossTradeCount?.toString() || "0",
        marketShares: row.marketShares?.toString() || "0",
        crossingShares: row.crossShares?.toString() || "0",
        marketDollars: row.marketDollars?.toString() || "0",
        crossingDollars: row.crossDollars?.toString() || "0",
      }));

      const totals = transformedData?.reduce(
        (acc, row) => {
          acc.tradeCount += Math.abs(row?.tradeCount);
          acc.shares += Math.abs(row?.shares);
          acc.dollars += parseInt(row?.dollars, 10);
          acc.marketTrades += Math.abs(row?.marketTrades || 0);
          acc.crossingTrades += Math.abs(row?.crossingTrades || 0);
          acc.marketShares += Math.abs(row?.marketShares || 0);
          acc.crossingShares += Math.abs(row?.crossingShares || 0);
          acc.marketDollars += parseInt(row?.marketDollars || 0, 10);
          acc.crossingDollars += parseInt(row?.crossingDollars || 0, 10);
          return acc;
        },
        {
          tradeCount: 0,
          shares: 0,
          dollars: 0,
          marketTrades: 0,
          crossingTrades: 0,
          marketShares: 0,
          crossingShares: 0,
          marketDollars: 0,
          crossingDollars: 0
        }
      );

      const totalRow = {
        type: "Total",
        tradeCount: totals?.tradeCount?.toString() || "0",
        shares: totals?.shares?.toString() || "0",
        dollars: totals?.dollars?.toString() || "0",
        marketTrades: totals?.marketTrades?.toString() || "0",
        crossingTrades: totals?.crossingTrades?.toString() || "0",
        marketShares: totals?.marketShares?.toString() || "0",
        crossingShares: totals?.crossingShares?.toString() || "0",
        marketDollars: totals?.marketDollars?.toString() || "0",
        crossingDollars: totals?.crossingDollars?.toString() || "0",
      };

      setData([...transformedData, totalRow]);
      setStrategyData(rebalancerSummary[selectedFundId]?.strategyTotals || {});
    }
  }, [order, orderBy, selectedFundId, selectedStrategyId, rebalancerSummary]);

  // Check if we're loading summary data
  const isLoadingSummary = activeRequests.includes("rebalancer_summary");

  // Show skeleton while loading
  if (isLoadingSummary) {
    return <SummaryTableSkeleton />;
  }
  return (
    <Box
      sx={{
        border: `100px ${theme.palette.lightGrey.tableHeader}`,
        borderRadius: "4px",
        marginTop: "1px",
        width: "100%",
        marginRight: 1,
      }}
    >
      <Box
        sx={{
          maxHeight: "650px",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "5px",
            height: "3px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f0f0f0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c0c0c0",
            borderRadius: "10px",
            minHeight: "2px",
          },
        }}
      >
        <Box sx={{ width: "40%" }}>
          <Box
            sx={{
              marginBottom: 1,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <select
                value={selectedFundId}
                onChange={handleFundIdChange}
                style={{ padding: "5px", fontSize: "14px" }}
                size="small"
              >
                <option value="All">All</option>
                {Object.keys(rebalancerSummary).map((fundId) => (
                  <option key={fundId} value={fundId}>
                    {findFundAbbById(fundId, funds)}
                  </option>
                ))}
              </select>
              

              <select
                value={selectedStrategyId}
                onChange={handleStrategyIdChange}
                disabled={selectedFundId === "All"}
                style={{ 
                  padding: "5px", 
                  fontSize: "14px",
                  opacity: selectedFundId === "All" ? 0.5 : 1,
                  cursor: selectedFundId === "All" ? "not-allowed" : "pointer"
                }}
                size="small"
              >
                <option value="All">All Strategies</option>
                {selectedFundId !== "All" && 
                  Object.keys(strategyData)
                    ?.sort((keyA, keyB) =>
                      findStrategy(keyA, strategies)?.localeCompare(
                        findStrategy(keyB, strategies)
                      )
                    )
                    ?.map((strategyId) => (
                      <option key={strategyId} value={strategyId}>
                        {findStrategy(strategyId, strategies)}
                      </option>
                    ))
                }
              </select>
              
         
           
            </Box>
          </Box>
        </Box>

        <Table
          size="small"
          stickyHeader
          aria-label="a dense table"
          sx={{
            borderCollapse: "collapse",
            border: "solid 1px #eee",
            marginTop: "-1px",
          }}
        >
          <thead>
            <TableRow>
              <TableCell
                colSpan={10}
              align="center"
              style={{
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[500],
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                padding: "8px 0",
                color: theme.palette.text.primary,
              }}
            >
              SUMMARY
              </TableCell>
            </TableRow>
            {/* First header row - merged categories */}
            <TableRow sx={{ background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100] }}>
              <TableCell
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Type
              </TableCell>
              <TableCell
                colSpan={3}
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Trade Count
              </TableCell>
              <TableCell
                colSpan={3}
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Shares
              </TableCell>
              <TableCell
                colSpan={3}
                align="center"
                style={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  padding: "4px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  backgroundColor: theme.palette.lightGrey.tableHeader,
                  color:  theme.palette.text.primary,
                }}
              >
                Dollars
              </TableCell>
            </TableRow>
            {/* Second header row - individual column headers */}
            <TableRow sx={{ background: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200] }}>
              <TableCell
                align="center"
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                }}
              >
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "marketTrades" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "marketTrades")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Market
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "crossingTrades" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "crossingTrades")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Crossing
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "tradeCount" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "tradeCount")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Total
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "marketShares" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "marketShares")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Market
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "crossingShares" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "crossingShares")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Crossing
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "shares" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "shares")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Total
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "marketDollars" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "marketDollars")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Market
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "crossingDollars" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "crossingDollars")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Crossing
                  </Typography>
                </Box>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "dollars" ? order : false}
                style={{
                  fontSize: "11px",
                  padding: "2px",
                  borderLeft: "0.1px solid #ccc3c3",
                  borderRight: "0.1px solid #ccc3c3",
                  color: theme.palette.text.primary,
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.lightGrey.tableHeader,
                  },
                }}
                onClick={() => handleRequestSort(null, "dollars")}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Typography sx={{ color: "black !important", fontSize: "11px", textAlign: "center" }}>
                    Total
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </thead>

          <TableBody>
            {data?.map((row, index) => (
              <React.Fragment key={index}>
                <TableRow
                  onClick={() => handleRowClick(index)}
                  sx={{
                    "& > .MuiTableCell-root": {
                      paddingTop: "1px",
                      paddingBottom: "0px !important",
                      lineHeight: "1.2",
                      "& .MuiInputBase-input": {
                        padding: "0px 0px !important",
                      },
                    },
                    "&:hover": {
                      background: theme.palette.grey[200],
                      cursor: "pointer",
                    },
                    backgroundColor:
                      row.type === "Total"
                        ? theme.palette.grey[200]
                        : "inherit",
                  }}
                >
                  <Tablecell
                    align="left"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: "bold",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {row.type}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.marketTrades)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.crossingTrades)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.tradeCount)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.marketShares)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.crossingShares)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.shares)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.marketDollars)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.crossingDollars)}
                  </Tablecell>
                  <Tablecell
                    align="right"
                    style={{
                      fontSize: "12px",
                      paddingLeft: "5px",
                      borderLeft: "none",
                      borderRight: "none",
                      fontWeight: row.type === "Total" ? "bold" : "normal",
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {formatNumber(row.dollars)}
                  </Tablecell>
                </TableRow>
                {expandedRow === index && (() => {
                  return Object.entries(strategyData)
                    ?.sort(([keyA], [keyB]) =>
                      findStrategy(keyA, strategies)?.localeCompare(
                        findStrategy(keyB, strategies)
                      )
                    )?.map(([key, value]) => (
                      <TableRow key={key} >
                        <Tablecell
                          style={{
                            paddingLeft: "5px",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {findStrategy(key, strategies)}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.marketTradeCount
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.crossTradeCount
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row.type)?.tradeCount
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.marketShares
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.crossShares
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row.type)?.shares
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.marketDollars
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.crossDollars
                          )}
                        </Tablecell>
                        <Tablecell
                          align="right"
                          style={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            borderLeft: "none",
                            borderRight: "none",
                            borderColor: theme.palette.divider,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatNumber(
                            value?.find((i) => i?.type === row?.type)?.dollars
                          )}
                        </Tablecell>
                      </TableRow>
                    ));
                })()}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default SummaryTable;
